package ui.app.assign.detail.definition

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import api.ResourceType
import api.ResourceType.TEXT.and
import apps.App
import compose.assets.Svg
import compose.assets.svg.Close
import compose.assets.svg.Plus
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_bottom_detail__gallery_action_add_media
import ssoft.frontend.generated.resources.layer_bottom_detail__video_volume_default_label
import ssoft.frontend.generated.resources.layer_bottom_detail__video_volume_max_label
import tk.mallumo.compose.navigation.navTo_ResourceUI
import tools.GlobalObjectShare
import tools.rememberString
import ui.app.assign.detail.AppDetailScope
import ui.resource.ResourceListingArgs
import ui.resource.detail.ResourceArgs
import ui.widget.LazyImage
import ui.widget.Space

@Composable
fun AppDetailScope.AppDefOfGallery(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfGallery
) {
    if (display.isMobile) {
        Column {
            DefaultFields(
                nnvl = wrapper,
                modifier = Modifier.fillMaxWidth(),
                mainContent = {
                    ContentFields(wrapper, nnvl)
                })
        }
    } else {
        Row {
            Column(Modifier.weight(1F)) {
                DefaultFields(
                    nnvl = wrapper,
                    modifier = Modifier.fillMaxWidth()
                )
            }
            Space(16.dp)
            Column(Modifier.weight(1F)) {
                ContentFields(
                    wrapper = wrapper,
                    nnvl = nnvl
                )
            }
        }
    }
    Space(64.dp)
}

@Composable
private fun AppDetailScope.ContentFields(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfGallery
) {
    VolumeControl(wrapper, nnvl)
    Space(16.dp)
}


@Composable
private fun AppDetailScope.VolumeControl(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfGallery,
    modifier: Modifier = Modifier.fillMaxWidth()
) {

    Card(modifier = modifier) {
        Column(
            Modifier.padding(16.dp)
                .fillMaxWidth()
        ) {
            Text(Res.string.layer_bottom_detail__video_volume_default_label.rememberString())
            Space(8.dp)
            Card(colors = CardDefaults.cardColors(MaterialTheme.colorScheme.inverseSurface)) {
                PercentageEntry(nnvl.volume, wrapper.canEdit)

            }
            Space(8.dp)
            Text(Res.string.layer_bottom_detail__video_volume_max_label.rememberString())
            Space(8.dp)
            Card(colors = CardDefaults.cardColors(MaterialTheme.colorScheme.inverseSurface)) {
                PercentageEntry(nnvl.volumeMax, wrapper.canEdit)
            }
        }
    }
}

@Composable
fun AppDetailScope.AppDefOfGalleryMedia(
    nnvl: AppDetailScope.WrapperDefinition.OfGallery
) {
    val imagePickerKey = remember(args.appId) {
        "AppDefOfGallery:${args.appId}"
    }

    GlobalObjectShare.Consumer(imagePickerKey)

    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = imagePickerKey,
            nav = nav,
            args = ResourceListingArgs(
                companyId = args.companyId,
                restrictionType = ResourceType.IMAGE and ResourceType.VIDEO,
                excluded = nnvl.media.state.joinToString(",") { it.key }
            )
        ) {
            it.firstOrNull()?.apply {
                fun recalculatePosition() {
                    nnvl.media.state.forEachIndexed { index, item ->
                        item.data.position = index
                    }
                }
                when (ResourceType[type]) {
                    ResourceType.IMAGE -> {
                        nnvl.media.add(
                            index = 0,
                            newItem = App.Gallery.Media.Image(
                                id = id,
                                width = width,
                                height = height,
                                position = -1
                            )
                        )
                        recalculatePosition()
                    }

                    ResourceType.VIDEO -> {
                        nnvl.media.add(
                            index = 0,
                            newItem = App.Gallery.Media.Video(
                                id = id,
                                width = width,
                                height = height,
                                position = -1,
                                duration = duration
                            )
                        )
                        recalculatePosition()
                    }

                    ResourceType.VIDEO_ALPHA,
                    ResourceType.DOCUMENT,
                    ResourceType.TEXT,
                    ResourceType.UNDEFINED,
                    ResourceType.ICON,
                    ResourceType.AUDIO,
                    ResourceType.RAW,
                    ResourceType.WWW_APP -> {
                        error("??")
                    }
                }
            }
        }
    }

    Column(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Button(
            onClick = { openResourcePicker() },
            modifier = Modifier
                .padding(horizontal = 16.dp, vertical = 8.dp)
                .padding(top = 8.dp)
                .fillMaxWidth()
        ) {
            Icon(Svg.Plus, null)
            Space(4.dp)
            Text(Res.string.layer_bottom_detail__gallery_action_add_media.rememberString())
        }
        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.Fixed(3),
            verticalItemSpacing = 8.dp,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(16.dp),
        ) {
            items(nnvl.media.state) {
                AppDefOfGalleryMediaEntry(nnvl, it)
            }
        }
    }
}

@Composable
fun AppDetailScope.AppDefOfGalleryMediaEntry(
    nnvl: AppDetailScope.WrapperDefinition.OfGallery,
    item: AppDetailScope.WrapperDefinition.OfGallery.Media.Item
) {
    Card(
        onClick = {
            nav.navTo_ResourceUI(
                ResourceArgs(
                    companyId = args.companyId,
                    resourceId = item.data.id
                )
            )
        },
        modifier = Modifier.fillMaxWidth()
            .aspectRatio(item.data.width.toFloat() / item.data.height.toFloat())
    ) {
        Box(Modifier.fillMaxSize()) {
            LazyImage(
                id = item.data.id,
                alphaBackground = false,
                contentScale = ContentScale.Fit,
                modifier = Modifier.fillMaxWidth()
                    .aspectRatio(item.data.width.toFloat() / item.data.height.toFloat())
            )
            SmallFloatingActionButton(
                modifier = Modifier.align(Alignment.TopEnd),
                onClick = {
                    nnvl.media.removeAll { it.key == item.key }
                }) { Icon(Svg.Close, null) }
        }
    }
}
